import { lazy, Suspense, useCallback, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { canApproveExpenses, isEmployee, useSession } from './auth/session';
import { FirstBusiness, SignIn } from './auth/SignIn';
import { pendingInviteToken } from './auth/invite';
import { pendingCheckout } from './auth/checkout';
import { LinkCheckout } from './pages/billing/Subscription';
import { Shell } from './layout/Shell';
import { Loading, PageHead } from './ui/kit';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Invoices = lazy(() => import('./pages/Invoices'));
const InvoiceDetail = lazy(() => import('./pages/InvoiceDetail'));
const DocumentEditor = lazy(() => import('./pages/DocumentEditor'));
const Bills = lazy(() => import('./pages/Bills'));
const Quotes = lazy(() => import('./pages/Quotes'));
const QuoteDetail = lazy(() => import('./pages/QuoteDetail'));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'));
const PurchaseOrderDetail = lazy(() => import('./pages/PurchaseOrderDetail'));
const BillDetail = lazy(() => import('./pages/BillDetail'));
const Contacts = lazy(() => import('./pages/Contacts'));
const ContactDetail = lazy(() => import('./pages/ContactDetail'));
const Items = lazy(() => import('./pages/Items'));
const Banking = lazy(() => import('./pages/Banking'));
const AddBankAccounts = lazy(() => import('./pages/banking/AddBankAccounts'));
const Reconcile = lazy(() => import('./pages/Reconcile'));
const Accounts = lazy(() => import('./pages/Accounts'));
const JournalNew = lazy(() => import('./pages/JournalNew'));
const Journals = lazy(() => import('./pages/Journals'));
const JournalEntry = lazy(() => import('./pages/JournalDetail'));
const Reports = lazy(() => import('./pages/Reports'));
const Report = lazy(() => import('./pages/Report'));
const Settings = lazy(() => import('./pages/Settings'));
const Account = lazy(() => import('./pages/Account'));
const GroupReport = lazy(() => import('./pages/groups/GroupReport'));
const GroupSetup = lazy(() => import('./pages/groups/GroupSetup'));
const AcceptInvite = lazy(() => import('./pages/team/AcceptInvite'));
const CreditList = lazy(() => import('./pages/credits/CreditList').then((m) => ({ default: m.CreditList })));
const CreditDetail = lazy(() => import('./pages/credits/CreditDetail').then((m) => ({ default: m.CreditDetailPage })));
const PaymentList = lazy(() => import('./pages/payments/PaymentList').then((m) => ({ default: m.PaymentList })));
const MyExpenses = lazy(() => import('./pages/expenses/MyExpenses'));
const ExpenseEditor = lazy(() => import('./pages/expenses/ExpenseEditor'));
const ExpenseDetail = lazy(() => import('./pages/expenses/ExpenseDetail'));
const ExpenseReview = lazy(() => import('./pages/expenses/Review'));
const Reimburse = lazy(() => import('./pages/expenses/Reimburse'));
const TripPage = lazy(() => import('./pages/expenses/TripPage'));
const TravelRates = lazy(() => import('./pages/expenses/TravelRates'));
const Vault = lazy(() => import('./pages/vault/Vault'));
const VaultDocumentPage = lazy(() => import('./pages/vault/VaultDocumentPage'));
const PaymentDetail = lazy(() => import('./pages/payments/PaymentDetail').then((m) => ({ default: m.PaymentDetailPage })));

function NotFound() {
  return <PageHead title="Page not found" sub="That page does not exist, or it is not part of MyBooks yet." crumb={{ to: '/', label: 'Dashboard' }} />;
}

export function App() {
  const session = useSession();
  // An invitation link, kept through signing in or creating an account (auth/invite.ts).
  const [invite, setInvite] = useState(() => pendingInviteToken());
  const inviteDone = useCallback(() => setInvite(null), []);
  // A subscription paid for at Stripe, kept through signing in or creating an account (auth/checkout.ts).
  const [checkout, setCheckout] = useState(() => pendingCheckout());
  const checkoutDone = useCallback(() => setCheckout(null), []);
  if (session.status === 'loading') return <div className="auth-page"><Loading label="Signing you in" /></div>;
  if (session.status === 'signed-out') return <SignIn invited={!!invite} checkout={checkout} />;
  if (invite) return <Suspense fallback={<div className="auth-page"><Loading /></div>}><AcceptInvite token={invite} onDone={inviteDone} /></Suspense>;
  if (checkout) return <LinkCheckout id={checkout} onDone={checkoutDone} />;
  if (!session.tenant) return <FirstBusiness />;
  const role = session.tenant.role;
  const expenseRoutes = (
    <>
      <Route path="expenses" element={<S><MyExpenses /></S>} />
      <Route path="expenses/new" element={<S><ExpenseEditor /></S>} />
      <Route path="expenses/:id" element={<S><ExpenseDetail /></S>} />
      <Route path="expenses/:id/edit" element={<S><ExpenseEditor /></S>} />
      <Route path="expenses/trips/new" element={<S><TripPage /></S>} />
      <Route path="expenses/trips/:id" element={<S><TripPage /></S>} />
    </>
  );
  // An employee has only their own expenses (ADR 0023); every other address leads back to them.
  if (isEmployee(role)) {
    return (
      <BrowserRouter>
        <Suspense fallback={null}>
          <Routes>
            <Route element={<Shell key={session.tenant.id} />}>
              <Route index element={<Navigate to="/expenses" replace />} />
              {expenseRoutes}
              <Route path="*" element={<Navigate to="/expenses" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }
  return (
    <BrowserRouter>
      <Suspense fallback={null}>
        <Routes>
          {/* Keyed by business so every screen starts fresh after switching. */}
          <Route element={<Shell key={session.tenant.id} />}>
            <Route index element={<S><Dashboard /></S>} />
            <Route path="invoices" element={<S><Invoices /></S>} />
            <Route path="invoices/new" element={<S><DocumentEditor kind="invoice" /></S>} />
            <Route path="invoices/:id" element={<S><InvoiceDetail /></S>} />
            <Route path="invoices/:id/edit" element={<S><DocumentEditor kind="invoice" /></S>} />
            <Route path="quotes" element={<S><Quotes /></S>} />
            <Route path="quotes/new" element={<S><DocumentEditor kind="quote" /></S>} />
            <Route path="quotes/:id" element={<S><QuoteDetail /></S>} />
            <Route path="quotes/:id/edit" element={<S><DocumentEditor kind="quote" /></S>} />
            <Route path="purchase-orders" element={<S><PurchaseOrders /></S>} />
            <Route path="purchase-orders/new" element={<S><DocumentEditor kind="purchase_order" /></S>} />
            <Route path="purchase-orders/:id" element={<S><PurchaseOrderDetail /></S>} />
            <Route path="purchase-orders/:id/edit" element={<S><DocumentEditor kind="purchase_order" /></S>} />
            <Route path="bills" element={<S><Bills /></S>} />
            <Route path="bills/new" element={<S><DocumentEditor kind="bill" /></S>} />
            <Route path="bills/:id" element={<S><BillDetail /></S>} />
            <Route path="bills/:id/edit" element={<S><DocumentEditor kind="bill" /></S>} />
            <Route path="credit-notes" element={<S><CreditList kind="credit_note" /></S>} />
            <Route path="credit-notes/new" element={<S><DocumentEditor kind="credit_note" /></S>} />
            <Route path="credit-notes/:id" element={<S><CreditDetail kind="credit_note" /></S>} />
            <Route path="credit-notes/:id/edit" element={<S><DocumentEditor kind="credit_note" /></S>} />
            <Route path="vendor-credits" element={<S><CreditList kind="vendor_credit" /></S>} />
            <Route path="vendor-credits/new" element={<S><DocumentEditor kind="vendor_credit" /></S>} />
            <Route path="vendor-credits/:id" element={<S><CreditDetail kind="vendor_credit" /></S>} />
            <Route path="vendor-credits/:id/edit" element={<S><DocumentEditor kind="vendor_credit" /></S>} />
            <Route path="payments-received" element={<S><PaymentList side="sales" /></S>} />
            <Route path="payments-received/:id" element={<S><PaymentDetail kind="received" /></S>} />
            <Route path="payments-made" element={<S><PaymentList side="purchases" /></S>} />
            <Route path="payments-made/:id" element={<S><PaymentDetail kind="made" /></S>} />
            <Route path="customer-refunds" element={<Navigate to="/payments-received?view=refunds" replace />} />
            <Route path="customer-refunds/:id" element={<S><PaymentDetail kind="customer_refund" /></S>} />
            <Route path="vendor-refunds" element={<Navigate to="/payments-made?view=refunds" replace />} />
            <Route path="vendor-refunds/:id" element={<S><PaymentDetail kind="vendor_refund" /></S>} />
            <Route path="customers" element={<S><Contacts type="customer" /></S>} />
            <Route path="vendors" element={<S><Contacts type="vendor" /></S>} />
            <Route path="contacts/:id" element={<S><ContactDetail /></S>} />
            <Route path="items" element={<S><Items /></S>} />
            <Route path="banking" element={<S><Banking /></S>} />
            <Route path="banking/add" element={<S><AddBankAccounts /></S>} />
            <Route path="reconcile" element={<S><Reconcile /></S>} />
            <Route path="accounts" element={<S><Accounts /></S>} />
            <Route path="journals" element={<S><Journals /></S>} />
            <Route path="journals/new" element={<S><JournalNew /></S>} />
            <Route path="journals/:id" element={<S><JournalEntry /></S>} />
            <Route path="reports" element={<S><Reports /></S>} />
            <Route path="reports/group" element={<S><GroupReport /></S>} />
            <Route path="reports/:key" element={<S><Report /></S>} />
            <Route path="groups/new" element={<S><GroupSetup /></S>} />
            <Route path="groups/:id" element={<S><GroupSetup /></S>} />
            <Route path="settings" element={<S><Settings /></S>} />
            <Route path="account" element={<S><Account /></S>} />
            {/* The document vault (ADR 0031); not in the employee's routes above. */}
            <Route path="vault" element={<S><Vault /></S>} />
            <Route path="vault/:id" element={<S><VaultDocumentPage /></S>} />
            {expenseRoutes}
            {canApproveExpenses(role) && <Route path="expenses/review" element={<S><ExpenseReview /></S>} />}
            {canApproveExpenses(role) && <Route path="expenses/reimburse" element={<S><Reimburse /></S>} />}
            {canApproveExpenses(role) && <Route path="expenses/rates" element={<S><TravelRates /></S>} />}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

/** Each page loads its code on first visit. */
function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}
